import { TransferUpDownIcon } from '@approvalmax/ui';
import { Button, Flex, Form, Grid, Popup, Progress, Select, Text } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';
import { FixedSide } from 'shared/data/wallets/v1';
import { useWalletAvailableCurrencies } from 'shared/hooks/(wallets)';
import { z } from 'zod';

import { ConvertAmountInputs } from '../ConvertAmountInputs/ConvertAmountInputs';
import { convertCurrencyPopupState } from '../ConvertCurrencyPopup/ConvertCurrencyPopup.states';
import { CurrencyMenuItem } from '../CurrencyMenuItem/CurrencyMenuItem';
import { filterAvailableCurrencies } from './ConvertCurrencyPopupContent.helpers';
import { useConversionRate, useCurrenciesInfo, useSubmitConversion } from './ConvertCurrencyPopupContent.hooks';
import { messages } from './ConvertCurrencyPopupContent.messages';
import { schema } from './ConvertCurrencyPopupContent.schema';
import { Balance, SelectWrapper } from './ConvertCurrencyPopupContent.styles';
import { ConvertCurrencyPopupContentProps } from './ConvertCurrencyPopupContent.types';

export const ConvertCurrencyPopupContent: FC<ConvertCurrencyPopupContentProps> = (props) => {
    const { onSubmit } = props;

    const popupState = useAtomValue(convertCurrencyPopupState);

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
    });

    useEffect(() => {
        if (popupState.fromDefaultCurrency) {
            form.setValue('fromCurrency', popupState.fromDefaultCurrency);
        }

        if (popupState.toDefaultCurrency) {
            form.setValue('toCurrency', popupState.toDefaultCurrency);
        }
    }, [popupState, form]);

    const { currencies, isLoading: isLoadingCurrencies } = useWalletAvailableCurrencies({
        needBalance: true,
        walletFilter: filterAvailableCurrencies,
    });

    const [fromCurrency, toCurrency] = form.watch(['fromCurrency', 'toCurrency']);

    const { rate, rateDate, isLoading: isLoadingRate } = useConversionRate(fromCurrency, toCurrency);

    const { IconFromFlag, IconToFlag, fromBalance, toBalance } = useCurrenciesInfo(
        fromCurrency,
        toCurrency,
        currencies
    );

    const { handleSubmitConversion, isLoading } = useSubmitConversion(fromBalance ?? 0, onSubmit);

    const fromCurrenciesList = useMemo(() => {
        if (!toCurrency) {
            return currencies;
        }

        return currencies?.filter((item) => item.id !== toCurrency);
    }, [currencies, toCurrency]);

    const toCurrenciesList = useMemo(() => {
        if (!fromCurrency) {
            return currencies;
        }

        return currencies?.filter((item) => item.id !== fromCurrency);
    }, [currencies, fromCurrency]);

    const handleCurrencySwap = useCallback(() => {
        const currentFromCurrency = form.getValues('fromCurrency');
        const currentToCurrency = form.getValues('toCurrency');

        form.setValue('fromCurrency', currentToCurrency);
        form.setValue('toCurrency', currentFromCurrency);
    }, [form]);

    const handleSubmit = form.handleSubmit((values) => {
        const fixedSide = values.fixedSide ?? FixedSide.sell;

        const amount = fixedSide === FixedSide.sell ? values.fromAmount : values.toAmount;

        if (!amount) {
            return;
        }

        handleSubmitConversion({
            fromCurrency: values.fromCurrency,
            toCurrency: values.toCurrency,
            amount,
            fixedSide,
        });
    });

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Popup.Header
                title={messages.title}
                actions={
                    <Button color='blue80' size='medium' type='submit' progress={isLoading}>
                        {messages.convert}
                    </Button>
                }
            />

            <Popup.Body>
                <Grid rowGap={16}>
                    <Text font='body' fontSize='medium'>
                        {messages.description}
                    </Text>

                    <SelectWrapper>
                        <Select
                            name='fromCurrency'
                            label={messages.fromCurrency}
                            placeholder={messages.selectCurrencyPlaceholder}
                            items={fromCurrenciesList ?? []}
                            progress={isLoadingCurrencies}
                            customMenuItem={(item) => (
                                <CurrencyMenuItem {...item} subName={item.currencyName} showBalance />
                            )}
                            autocomplete
                            clearable={false}
                            itemNameKey='id'
                            startIcon={IconFromFlag ? <IconFromFlag width={22} /> : undefined}
                        />

                        {fromCurrency && (
                            <Balance>
                                <Text font='label' fontSize='medium' color='midnight70'>
                                    {intl.formatNumber(fromBalance)}
                                </Text>
                            </Balance>
                        )}
                    </SelectWrapper>

                    <SelectWrapper>
                        <Select
                            name='toCurrency'
                            label={messages.toCurrency}
                            placeholder={messages.selectCurrencyPlaceholder}
                            items={toCurrenciesList ?? []}
                            progress={isLoadingCurrencies}
                            customMenuItem={(item) => (
                                <CurrencyMenuItem {...item} subName={item.currencyName} showBalance />
                            )}
                            autocomplete
                            clearable={false}
                            itemNameKey='id'
                            startIcon={IconToFlag ? <IconToFlag width={22} /> : undefined}
                        />

                        {toCurrency && (
                            <Balance>
                                <Text font='label' fontSize='medium' color='midnight70'>
                                    {intl.formatNumber(toBalance)}
                                </Text>
                            </Balance>
                        )}
                    </SelectWrapper>

                    <Flex justifyContent='end'>
                        <Button
                            disabled={!fromCurrency || !toCurrency || isLoading}
                            color='blue10'
                            size='xsmall'
                            endIcon={<TransferUpDownIcon size={12} />}
                            onClick={handleCurrencySwap}
                        >
                            {messages.swap}
                        </Button>
                    </Flex>

                    <ConvertAmountInputs rate={rate} />

                    <Flex alignItems='center' spacing='4'>
                        {fromCurrency && toCurrency && (
                            <Text color='midnight70' font='label' fontSize='small'>
                                {messages.exchangeRate({
                                    fromCurrency,
                                    toCurrency,
                                    date: isLoadingRate || !rateDate ? <Skeleton inline width={100} /> : rateDate,
                                    rate:
                                        isLoadingRate || typeof rate !== 'number' ? (
                                            <Skeleton inline width={50} />
                                        ) : (
                                            intl.formatNumber(rate, 'inherit')
                                        ),
                                })}
                            </Text>
                        )}

                        {isLoadingRate && <Progress size='uxsmall' shape='circle' />}
                    </Flex>
                </Grid>
            </Popup.Body>
        </Form>
    );
};

ConvertCurrencyPopupContent.displayName = 'ConvertCurrencyPopupContent';
